import React, { useMemo } from 'react';
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';

interface DatePatternDaysTableProps {
    dates: string[];
    loading?: boolean;
    emptyMessage?: string;
    maxHeight?: number;
}

const toDisplayDate = (isoDate: string): string => {
    const parts = isoDate.split('-');
    if (parts.length !== 3) {
        return isoDate;
    }

    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const toWeekdayName = (isoDate: string): string => {
    const parsedDate = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(parsedDate.getTime())) {
        return '-';
    }

    return new Intl.DateTimeFormat('es-ES', {
        weekday: 'long',
        timeZone: 'UTC'
    }).format(parsedDate);
};

const DatePatternDaysTable: React.FC<DatePatternDaysTableProps> = ({
    dates,
    loading = false,
    emptyMessage = 'No hay fechas para mostrar',
    maxHeight = 420
}) => {
    const rows = useMemo(() => dates.map((date) => ({
        date,
        displayDate: toDisplayDate(date),
        weekday: toWeekdayName(date)
    })), [dates]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {emptyMessage}
            </Typography>
        );
    }

    return (
        <TableContainer sx={{ maxHeight }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Día</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.date} hover>
                            <TableCell>{row.displayDate}</TableCell>
                            <TableCell>{row.weekday}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export { DatePatternDaysTable };
